import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable, Pagination } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { dt, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function DoNotCall() {
  const { user, can } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [draft, setDraft] = useState({ phone: "", reason: "", notes: "", country: "AL" });
  const [check, setCheck] = useState({ phone: "", result: null });

  const { data, isLoading } = useQuery({
    queryKey: ["dnc", page, q],
    queryFn: () => get("/dnc", { page, page_size: 25, q: q || undefined }),
  });

  const add = useMutation({
    mutationFn: () => api.post("/dnc", draft),
    onSuccess: () => {
      toast.success("Number added to the do-not-call registry");
      setOpen(false);
      setDraft({ phone: "", reason: "", notes: "", country: "AL" });
      queryClient.invalidateQueries({ queryKey: ["dnc"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const remove = useMutation({
    mutationFn: ({ id, reason }) => api.delete(`/dnc/${id}`, { params: { reason } }),
    onSuccess: () => {
      toast.success("Entry removed and recorded in the audit log");
      setRemoving(null);
      queryClient.invalidateQueries({ queryKey: ["dnc"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const runCheck = useMutation({
    mutationFn: () => get(`/dnc/check/${encodeURIComponent(check.phone)}`),
    onSuccess: (result) => setCheck((c) => ({ ...c, result })),
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <>
      <PageHeader
        testId="dnc-page"
        title="Do not call registry"
        subtitle="Enforced before every outbound call — only a Super Admin can remove an entry"
        actions={
          can("dnc", "write") ? (
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setOpen(true)} data-testid="dnc-add-button">
              Add number
            </Button>
          ) : null
        }
      />

      <Section title="Check a number" testId="dnc-check-section">
        <div className="flex flex-wrap items-end gap-3 p-4">
          <div className="space-y-1.5">
            <Label>Phone number</Label>
            <Input
              data-testid="dnc-check-input"
              value={check.phone}
              onChange={(e) => setCheck({ phone: e.target.value, result: null })}
              placeholder="+355 69 000 0000"
              className="w-56"
            />
          </div>
          <Button variant="outline" disabled={!check.phone} onClick={() => runCheck.mutate()} data-testid="dnc-check-button">
            Check status
          </Button>
          {check.result ? (
            <p data-testid="dnc-check-result" className={`text-sm font-medium ${check.result.blocked ? "text-rose-700" : "text-emerald-700"}`}>
              {check.result.phone} is {check.result.blocked ? "blocked — calls will not be placed" : "callable"}
            </p>
          ) : null}
        </div>
      </Section>

      <Section
        testId="dnc-section"
        title={`${data?.total ?? 0} blocked numbers`}
        actions={
          <Input data-testid="dnc-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search number or name" className="h-9 w-56" />
        }
      >
        <DataTable
          testId="dnc-table"
          rowTestId="dnc-row"
          loading={isLoading}
          columns={[
            { key: "phone", label: "Phone", render: (r) => <span className="font-mono text-xs font-medium text-slate-800">{r.phone}</span> },
            { key: "contact_name", label: "Contact", render: (r) => r.contact_name || "—" },
            { key: "reason", label: "Reason" },
            { key: "source", label: "Source", render: (r) => <StatusBadge value={r.source === "call_result" ? "do_not_call" : "draft"} label={title(r.source)} /> },
            { key: "country", label: "Country" },
            { key: "transcript_evidence", label: "Evidence", render: (r) => <span className="line-clamp-1 max-w-xs text-xs text-slate-600">{r.transcript_evidence || "—"}</span> },
            { key: "added_by", label: "Added by" },
            { key: "created_at", label: "Date added", render: (r) => dt(r.created_at) },
            {
              key: "actions",
              label: "",
              render: (r) =>
                user?.role === "super_admin" ? (
                  <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => setRemoving(r)} data-testid={`dnc-remove-${r.id}`}>
                    Remove
                  </Button>
                ) : null,
            },
          ]}
          rows={data?.items || []}
          empty={{ title: "The registry is empty", description: "Numbers are added automatically when a call outcome is “do not call”." }}
        />
        <Pagination page={data?.page || 1} pages={data?.pages || 1} total={data?.total || 0} onChange={setPage} />
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Add number to do-not-call registry</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Phone number</Label>
              <Input data-testid="dnc-phone-input" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Reason</Label>
              <Input data-testid="dnc-reason-input" value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input data-testid="dnc-notes-input" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={!draft.phone || !draft.reason} onClick={() => add.mutate()} data-testid="dnc-add-submit">
              Add to registry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        destructive
        requireReason
        reasonLabel="Removal reason (recorded in the audit log)"
        title={`Remove ${removing?.phone} from the registry?`}
        description="The contact becomes callable again. Only Super Admins can perform this action."
        confirmLabel="Remove entry"
        onConfirm={(reason) => remove.mutateAsync({ id: removing.id, reason })}
        testId="dnc-remove-dialog"
      />
    </>
  );
}
